import React from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { InfoOutlined as InfoOutlinedIcon } from '@mui/icons-material';
import ArticuloInfo360DocumentoList from './ArticuloInfo360DocumentoList';
import type { ArticuloInfo360Documento, ArticuloInfo360Indicadores } from '../types';
import { formatCantidad, resolveUnidadMedida } from '../formatCantidad';
import { partesUbicacionFisica } from '../../../../services/productoDepositoService';

const COL_TOOLTIPS = {
  deposito: 'Depósito o almacén donde está el stock.',
  ubicacion: 'Posición física dentro del depósito (pasillo, estantería, etc.).',
  actual: 'Stock físico real en este depósito.',
  disponible: 'Lo que se puede vender ahora: stock físico menos lo reservado en pedidos.',
  reservado: 'Cantidad ya comprometida en pedidos de venta pendientes de entregar. No está libre para vender.',
  produccion: 'Cantidad en órdenes de producción en curso, todavía no ingresada al depósito.',
  oc: 'Cantidad en órdenes de compra pendientes de recibir.',
  atp: 'Available to Promise (disponible para promesa). Lo que se puede comprometer a un cliente: disponible + producción en curso + OC pendientes de recibir.',
} as const;

const METRIC_TOOLTIPS = {
  minimo: 'Umbral de stock mínimo configurado para este artículo.',
  stockNeto: 'Stock neto: físico menos reservado, más las OC pendientes de recibir.',
  maximo: 'Umbral de stock máximo configurado para este artículo.',
  disponibleVenta: 'Total que se puede vender ahora (físico menos reservado), sumando todos los depósitos.',
  reservado: 'Total comprometido en pedidos de venta pendientes de entregar.',
  ocPendiente: 'Total en órdenes de compra pendientes de recibir.',
  cobertura: 'Días de venta que cubre el stock neto con la demanda promedio de los últimos 6 meses.',
} as const;

const KIT_TOOLTIPS = {
  cantPorKit: 'Unidades de este componente necesarias para armar 1 kit.',
  stockDisp: 'Stock disponible del componente para armar kits.',
  kitsPosibles: 'Cuántos kits se pueden armar con el stock de este componente.',
  kitsEnsamblables: 'Kits que se pueden armar hoy: lo limita el componente con menos stock relativo.',
} as const;

function HeaderConTooltip({
  label,
  tooltip,
  align = 'right',
}: {
  label: string;
  tooltip: string;
  align?: 'left' | 'right';
}) {
  return (
    <Tooltip title={tooltip} arrow placement="top" enterTouchDelay={0}>
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          cursor: 'help',
          justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
          width: '100%',
        }}
      >
        {label}
        <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
      </Box>
    </Tooltip>
  );
}

function MetricConTooltip({
  label,
  tooltip,
  children,
}: {
  label: string;
  tooltip: string;
  children: React.ReactNode;
}) {
  return (
    <Typography variant="body2" component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
      <Tooltip title={tooltip} arrow placement="top" enterTouchDelay={0}>
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.35, cursor: 'help' }}>
          {label}
          <InfoOutlinedIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
        </Box>
      </Tooltip>
      {children}
    </Typography>
  );
}

interface Props {
  data: ArticuloInfo360Indicadores;
  onAbrirDocumento?: (documento: ArticuloInfo360Documento) => void;
  loadingUbicaciones?: boolean;
  loadingKitStock?: boolean;
}

const ArticuloInfo360TabStock: React.FC<Props> = ({
  data,
  onAbrirDocumento,
  loadingUbicaciones = false,
  loadingKitStock = false,
}) => {
  const um = resolveUnidadMedida(data.unidadMedida);
  const maxRef = Math.max(data.stockMax, data.stockNeto, data.stockMin, 1);
  const pctActual = Math.min(100, Math.max(0, (data.stockNeto / maxRef) * 100));
  const pctMin = data.stockMin > 0 ? Math.min(100, (data.stockMin / maxRef) * 100) : 0;

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
          Nivel de stock vs umbrales
        </Typography>
        <Stack spacing={1}>
          <Stack direction="row" justifyContent="space-between">
            <Tooltip title={METRIC_TOOLTIPS.minimo} arrow placement="top" enterTouchDelay={0}>
              <Typography variant="caption" color="text.secondary" sx={{ cursor: 'help' }}>
                Mínimo: {formatCantidad(data.stockMin, um)}
              </Typography>
            </Tooltip>
            <Tooltip title={METRIC_TOOLTIPS.stockNeto} arrow placement="top" enterTouchDelay={0}>
              <Typography variant="caption" color="text.secondary" sx={{ cursor: 'help' }}>
                Actual: {formatCantidad(data.stockNeto, um)}
              </Typography>
            </Tooltip>
            <Tooltip title={METRIC_TOOLTIPS.maximo} arrow placement="top" enterTouchDelay={0}>
              <Typography variant="caption" color="text.secondary" sx={{ cursor: 'help' }}>
                Máximo: {formatCantidad(data.stockMax, um)}
              </Typography>
            </Tooltip>
          </Stack>
          <Box sx={{ position: 'relative' }}>
            <LinearProgress
              variant="determinate"
              value={pctActual}
              sx={{ height: 10, borderRadius: 1 }}
            />
            {pctMin > 0 ? (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: `${pctMin}%`,
                  width: 2,
                  height: 10,
                  bgcolor: 'warning.main',
                }}
              />
            ) : null}
          </Box>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <MetricConTooltip label="Disponible venta:" tooltip={METRIC_TOOLTIPS.disponibleVenta}>
              <strong>{formatCantidad(data.stockDisponibleVenta, um)}</strong>
            </MetricConTooltip>
            <MetricConTooltip label="Reservado:" tooltip={METRIC_TOOLTIPS.reservado}>
              <strong>{formatCantidad(data.stockReservado, um)}</strong>
            </MetricConTooltip>
            <MetricConTooltip label="OC pendiente:" tooltip={METRIC_TOOLTIPS.ocPendiente}>
              <strong>{formatCantidad(data.stockPendienteOc, um)}</strong>
            </MetricConTooltip>
            <MetricConTooltip label="Cobertura:" tooltip={METRIC_TOOLTIPS.cobertura}>
              <strong>{data.coberturaDias >= 9999 ? '—' : `${Math.round(data.coberturaDias)} días`}</strong>
            </MetricConTooltip>
          </Stack>
        </Stack>
      </Paper>

      {loadingKitStock ? (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              Cargando disponibilidad del kit…
            </Typography>
          </Stack>
        </Paper>
      ) : null}

      {data.kitStock ? (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
            Disponibilidad del kit (por componentes)
          </Typography>
          <Box sx={{ mb: 1.5 }}>
            <MetricConTooltip label="Kits ensamblables:" tooltip={KIT_TOOLTIPS.kitsEnsamblables}>
              <strong>{formatCantidad(data.kitStock.disponible, um)}</strong>
            </MetricConTooltip>
          </Box>
          {data.kitStock.componentes.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              El kit no tiene componentes configurados.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Componente</TableCell>
                    <TableCell align="right">
                      <HeaderConTooltip label="Cant. por kit" tooltip={KIT_TOOLTIPS.cantPorKit} />
                    </TableCell>
                    <TableCell align="right">
                      <HeaderConTooltip label="Stock disp." tooltip={KIT_TOOLTIPS.stockDisp} />
                    </TableCell>
                    <TableCell align="right">
                      <HeaderConTooltip label="Kits posibles" tooltip={KIT_TOOLTIPS.kitsPosibles} />
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.kitStock.componentes.map((c) => (
                    <TableRow
                      key={c.codigoArticulo}
                      sx={c.esLimitante ? { bgcolor: 'warning.light' } : undefined}
                    >
                      <TableCell>
                        {c.codigoArticulo}
                        {c.descripcion ? ` — ${c.descripcion}` : ''}
                        {c.esLimitante ? (
                          <Chip size="small" label="Limita" color="warning" sx={{ ml: 1, height: 20 }} />
                        ) : null}
                      </TableCell>
                      <TableCell align="right">{formatCantidad(c.cantidadPorKit, um)}</TableCell>
                      <TableCell align="right">{formatCantidad(c.stockDisponible, um)}</TableCell>
                      <TableCell align="right">{formatCantidad(c.kitsPosibles, um)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      ) : null}

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            Stock por depósito{um ? ` (${um})` : ''}
          </Typography>
          {loadingUbicaciones ? (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <CircularProgress size={14} />
              <Typography variant="caption" color="text.secondary">
                Ubicaciones…
              </Typography>
            </Stack>
          ) : null}
        </Stack>
        {!Array.isArray(data.stockPorDeposito) || data.stockPorDeposito.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Sin detalle por depósito.
          </Typography>
        ) : (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <HeaderConTooltip label="Depósito" tooltip={COL_TOOLTIPS.deposito} align="left" />
                  </TableCell>
                  <TableCell>
                    <HeaderConTooltip label="Ubicación" tooltip={COL_TOOLTIPS.ubicacion} align="left" />
                  </TableCell>
                  <TableCell align="right">
                    <HeaderConTooltip label="Actual" tooltip={COL_TOOLTIPS.actual} />
                  </TableCell>
                  <TableCell align="right">
                    <HeaderConTooltip label="Disponible" tooltip={COL_TOOLTIPS.disponible} />
                  </TableCell>
                  <TableCell align="right">
                    <HeaderConTooltip label="Reservado" tooltip={COL_TOOLTIPS.reservado} />
                  </TableCell>
                  <TableCell align="right">
                    <HeaderConTooltip label="Prod." tooltip={COL_TOOLTIPS.produccion} />
                  </TableCell>
                  <TableCell align="right">
                    <HeaderConTooltip label="OC" tooltip={COL_TOOLTIPS.oc} />
                  </TableCell>
                  <TableCell align="right">
                    <HeaderConTooltip label="ATP" tooltip={COL_TOOLTIPS.atp} />
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.stockPorDeposito.map((dep) => {
                  const partes = partesUbicacionFisica(dep, dep.labels);
                  const ocDep = dep.stockOrdenCompra ?? dep.stockPendienteOcRecibir;
                  return (
                    <TableRow key={dep.nroUbicacion}>
                      <TableCell>{dep.nombreUbicacion || dep.nroUbicacion}</TableCell>
                      <TableCell>
                        {loadingUbicaciones && partes.length === 0 ? (
                          <Typography variant="caption" color="text.secondary">…</Typography>
                        ) : partes.length > 0 ? (
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            {partes.map((parte) => (
                              <Chip
                                key={parte.label}
                                size="small"
                                variant="outlined"
                                label={`${parte.label}: ${parte.value}`}
                                sx={{ height: 22, '& .MuiChip-label': { px: 0.75, fontSize: 12 } }}
                              />
                            ))}
                          </Stack>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell align="right">{formatCantidad(dep.cantidadActual, um)}</TableCell>
                      <TableCell align="right">{formatCantidad(dep.stockDisponibleVenta, um)}</TableCell>
                      <TableCell align="right">{formatCantidad(dep.stockReservado, um)}</TableCell>
                      <TableCell align="right">{formatCantidad(dep.stockProduccion, um)}</TableCell>
                      <TableCell align="right">{formatCantidad(ocDep, um)}</TableCell>
                      <TableCell align="right">{formatCantidad(dep.atp, um)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Stack spacing={1}>
            {data.reservaPedidoActiva === false && data.reservasPedidos.length > 0 ? (
              <Alert severity="info" sx={{ py: 0.5 }}>
                Hay pedidos pendientes, pero el tipo de formulario Pedido no tiene Descuenta stock activo:
                no comprometen el disponible ni el reservado.
              </Alert>
            ) : null}
            <ArticuloInfo360DocumentoList
              titulo="Reservas en pedidos"
              documentos={data.reservasPedidos}
              emptyText="No hay reservas en pedidos."
              onAbrirDocumento={onAbrirDocumento}
              mostrarCantidad
              unidadMedida={um}
            />
          </Stack>
        </Grid>
        <Grid item xs={12} md={6}>
          <ArticuloInfo360DocumentoList
            titulo="OC pendientes de recibir"
            documentos={data.ocPendientes}
            emptyText="No hay OC pendientes."
            onAbrirDocumento={onAbrirDocumento}
            mostrarCantidad
            unidadMedida={um}
          />
        </Grid>
      </Grid>
    </Stack>
  );
};

export default ArticuloInfo360TabStock;
